/**
 * Workflow de Transferencia EPP
 *
 * Ejecuta el flujo de transferencia de EPP después de crear el DTE:
 * 1. Espera a que el PDF del DTE esté listo
 * 2. Sube el PDF como archivo (doc_file)
 * 3. Ejecuta la transferencia de items usando el ID del archivo
 */

import {
	transferWarehouseItemTransfer,
	uploadFoldersFile,
	getFileTypeByCode,
} from '../../../../../slices/thunks';
import {
	readDTEFacele,
	createWarehouseGuide,
} from '../../../../../slices/warehouseGuide/thunk';

// Configuración de polling para esperar el PDF
const PDF_POLL_INTERVAL = 3000;
const PDF_POLL_MAX_ATTEMPTS = 10;

export class TransferenciaWorkflow {
	/**
	 * Ejecuta el workflow de transferencia EPP
	 * @param {Function} onProgress - Callback (stepId, status, message?) para reportar progreso
	 */
	static async execute(dispatch, dteResult, formValues, onProgress) {
		const dteId = dteResult?.data?._id;
		const folio = dteResult?.data?.folio || '';
		const rut =
			dteResult?.data?.dtefacele?.Encabezado?.Emisor?.RUTEmisor || '';

		// Variables para trackear recursos creados (para rollback)
		let documentId = null;
		let guideId = null;

		try {
			// 1. Esperar a que el PDF del DTE esté listo
			if (onProgress) onProgress('wf_pdf', 'loading');

			const pdfBlob = await TransferenciaWorkflow.waitForPDF(dispatch, dteId);

			if (!pdfBlob) {
				if (onProgress) onProgress('wf_pdf', 'error', 'No se pudo obtener el PDF');
				const error = new Error(
					'No se pudo obtener el PDF de la guía. La transferencia no se puede completar.'
				);
				error.createdResources = { warGuideId: guideId, documentId, folio };
				throw error;
			}
			if (onProgress) onProgress('wf_pdf', 'done');

			// 2. Subir el PDF como archivo
			if (onProgress) onProgress('wf_upload', 'loading');
			const fileName = `${rut}-T52-F${folio}`.replace(/\./g, '');
			documentId = await TransferenciaWorkflow.uploadDocument(
				dispatch,
				pdfBlob,
				fileName,
				formValues.establishment_id
			);

			if (!documentId) {
				if (onProgress) onProgress('wf_upload', 'error', 'Error al subir documento');
				const error = new Error(
					'No se pudo subir el documento de la guía. La transferencia no se puede completar.'
				);
				error.createdResources = { warGuideId: guideId, documentId, folio };
				throw error;
			}
			if (onProgress) onProgress('wf_upload', 'done');

			// 3. Crear registro en war_guide
			if (onProgress) onProgress('wf_guide', 'loading');
			let guideCreationFailed = false;
			try {
				const guideData = {
					folio: parseInt(folio),
					dte_id: dteId,
					document_id: documentId,
					establishment_id: formValues.establishment_id,
					guide_category: 'epp',
					operation_type: 'transferencia',
					warehouse_origin_id: formValues.warehouse_origin_id,
					warehouse_destination_id: formValues.warehouse_destination_id,
					items_payload: {
						items_detail: formValues.items,
					},
					mnt_neto: Math.round(Number(formValues.totals_mnt_neto) || 0),
					mnt_iva: Math.round(Number(formValues.totals_iva) || 0),
					mnt_total: Math.round(Number(formValues.totals_mnt_total) || 0),
					sap_code_origin: formValues._warehouse_origin_code || null,
					sap_code_destination: formValues._warehouse_destination_code || null,
					transfer_id: formValues.transfer_id || null,
					patente: formValues.transfer_patente_id || null,
					driver_rut: formValues.transfer_conductor_rut || null,
					guide_date: new Date().toISOString(),
					observations: formValues.observations || null,
					status_guide_id: 'active',
				};

				const guideResult = await dispatch(
					createWarehouseGuide(guideData)
				).unwrap();
				guideId = guideResult._id;
				if (onProgress) onProgress('wf_guide', 'done');
			} catch (error) {
				console.error('[TransferenciaWorkflow] Error creando guía:', error);
				guideCreationFailed = true;
				if (onProgress) onProgress('wf_guide', 'warning', 'Guía no registrada, pero transferencia continuará');
			}

			// 4. Mapear items del formulario al schema de transferencia
			const warehouseItems = TransferenciaWorkflow.mapItemsToTransferItems(
				formValues.items,
				formValues
			);

			// 5. Ejecutar transferencia con el document_id del archivo subido
			if (onProgress) onProgress('wf_transfer', 'loading');
			const transferData = {
				warehouse_items: warehouseItems,
				establishment_id: formValues.establishment_destination_id,
				warehouse_destination_id: formValues.warehouse_destination_id,
				warehouse_origin_id: formValues.warehouse_origin_id,
				document_id: documentId,
				document_number: String(folio),
				guide_id: guideId,
			};

			let transferResult;
			try {
				transferResult = await dispatch(
					transferWarehouseItemTransfer(transferData)
				).unwrap();
				if (onProgress) onProgress('wf_transfer', 'done');
			} catch (transferError) {
				// Marcar el step como error antes de lanzar
				if (onProgress) onProgress('wf_transfer', 'error', transferError.message || 'Error al ejecutar transferencia');
				const error = new Error(transferError.message || 'Error al ejecutar la transferencia');
				error.createdResources = { warGuideId: guideId, documentId, folio };
				throw error;
			}

			return {
				success: true,
				documentId,
				transferResult,
				guideId,
				guideCreationFailed,
				warningMessage: guideCreationFailed 
					? `Guía no registrada. Folio: ${folio}, Documento: ${documentId}`
					: null,
				createdResources: {
					warGuideId: guideId,
					documentId: documentId,
					folio: folio
				}
			};
		} catch (error) {
			// Asegurar que el error incluya recursos creados para rollback
			if (!error.createdResources) {
				error.createdResources = { warGuideId: guideId, documentId, folio };
			}
			throw error;
		}
	}

	/**
	 * Espera a que el PDF del DTE esté disponible usando polling
	 */
	static async waitForPDF(dispatch, dteId) {
		for (let attempt = 1; attempt <= PDF_POLL_MAX_ATTEMPTS; attempt++) {
			try {
				const response = await dispatch(
					readDTEFacele({ file_type: 'pdf', dte_id: dteId })
				).unwrap();

				const data = response?.data ?? response;

				if (data instanceof Blob) {
					const contentType =
						response?.headers?.['content-type'] || data.type || '';

					if (
						contentType.includes('application/pdf') ||
						data.size > 1000
					) {
						return data;
					}
				}
			} catch (err) {
				// Continuar polling si es 404 o no está listo
			}

			if (attempt < PDF_POLL_MAX_ATTEMPTS) {
				await new Promise(resolve =>
					setTimeout(resolve, PDF_POLL_INTERVAL)
				);
			}
		}

		console.warn('[TransferenciaWorkflow] Timeout esperando PDF');
		return null;
	}

	/**
	 * Sube el PDF como archivo doc_file
	 */
	static async uploadDocument(dispatch, pdfBlob, fileName, establishmentId) {
		try {
			const fileTypeResponse = await dispatch(
				getFileTypeByCode('GD')
			).unwrap();
			const fileTypeId = fileTypeResponse?._id;

			if (!fileTypeId) {
				console.error('[TransferenciaWorkflow] No se encontró el tipo de archivo GD');
				return null;
			}

			const file = new File([pdfBlob], `${fileName}.pdf`, {
				type: 'application/pdf',
			});

			const formData = new FormData();
			formData.append('file', file);
			formData.append('file_type_id', fileTypeId);
			if (establishmentId) {
				formData.append('establishment_id', establishmentId);
			}

			const folderPath = '/Abastecimiento/EPP/Transferencia';
			formData.append('folder_paths', folderPath);

			const response = await dispatch(uploadFoldersFile(formData)).unwrap();

			const uploadedFile = Array.isArray(response)
				? response[response.length - 1]
				: response;

			return uploadedFile?._id || null;
		} catch (error) {
			console.error('[TransferenciaWorkflow] Error subiendo documento:', error);
			return null;
		}
	}

	/**
	 * Mapea items del formulario de guía al schema de transferencia
	 */
	static mapItemsToTransferItems(items, formValues) {
		return items.map(item => ({
			warehouse_id: formValues.warehouse_origin_id,
			warehouse_code: item._module_vars?.warehouse_code || '',
			warehouse_group_id: item._module_vars?.warehouse_group_id || '',
			warehouse_group_code: item._module_vars?.warehouse_group_code || '',
			warehouse_item_id: item._module_vars?.warehouse_item_id || '',
			warehouse_category_name:
				item._module_vars?.warehouse_category_name || '',
			name: item.NmbItem,
			description: item.DscItem || '',
			quantity: Number(item.QtyItem),
			size: item._module_vars?.size || '',
			color: item._module_vars?.color || '',
			unit_measurement_name: item.UnmdItem,
			price: Number(item.PrcItem) || 0,
		}));
	}
}
